import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { CloseIcon, CheckIcon } from "./icons";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
  color?: string;
}

export interface ComboboxProps {
  label: string;
  options: ComboboxOption[];
  value?: string;
  values?: string[];
  onChange?: (value: string) => void;
  onValuesChange?: (values: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  name?: string;
  disabled?: boolean;
  className?: string;
  onCreate?: (query: string) => void;
}

/** Searchable single- or multi-select with listbox keyboard behavior. */
export function Combobox({
  label,
  options,
  value,
  values,
  onChange,
  onValuesChange,
  placeholder = "Search…",
  loading = false,
  name,
  disabled,
  className,
  onCreate,
}: ComboboxProps) {
  useLocaleSubscription();
  const id = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [listboxStyle, setListboxStyle] = useState<CSSProperties>({
    position: "fixed",
    insetInlineStart: 8,
    top: 8,
    width: 240,
    maxHeight: 240,
  });
  const selected = values ?? (value ? [value] : []);
  const multiple = values !== undefined;
  const filtered = useMemo(
    () =>
      options.filter((option) =>
        option.label
          .toLocaleLowerCase()
          .includes(query.trim().toLocaleLowerCase()),
      ),
    [options, query],
  );
  const enabledIndexes = useMemo(
    () => filtered.flatMap((option, index) => (option.disabled ? [] : [index])),
    [filtered],
  );

  useEffect(() => {
    setActiveIndex((index) =>
      enabledIndexes.includes(index) ? index : (enabledIndexes[0] ?? -1),
    );
  }, [enabledIndexes]);

  useLayoutEffect(() => {
    if (!open) return;

    function positionListbox(): void {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const gutter = 8;
      const gap = 6;
      const availableWidth = Math.max(0, window.innerWidth - gutter * 2);
      const width = Math.min(rect.width, availableWidth);
      const left = Math.min(
        Math.max(rect.left, gutter),
        window.innerWidth - gutter - width,
      );
      const below = Math.max(0, window.innerHeight - rect.bottom - gap - gutter);
      const above = Math.max(0, rect.top - gap - gutter);
      const placeAbove = below < 160 && above > below;
      const maxHeight = Math.min(240, placeAbove ? above : below);

      setListboxStyle({
        position: "fixed",
        insetInlineStart: left,
        width,
        maxHeight,
        ...(placeAbove
          ? { bottom: window.innerHeight - rect.top + gap, top: "auto" }
          : { top: rect.bottom + gap, bottom: "auto" }),
      });
    }

    positionListbox();
    // Embedded items can wrap while the list stays open.
    const observer = typeof ResizeObserver === "undefined"
      ? undefined
      : new ResizeObserver(positionListbox);
    if (anchorRef.current) observer?.observe(anchorRef.current);
    window.addEventListener("resize", positionListbox);
    window.addEventListener("scroll", positionListbox, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", positionListbox);
      window.removeEventListener("scroll", positionListbox, true);
    };
  }, [open]);

  function choose(option: ComboboxOption): void {
    if (option.disabled) return;
    if (multiple) {
      const next = selected.includes(option.value)
        ? selected.filter((item) => item !== option.value)
        : [...selected, option.value];
      onValuesChange?.(next);
    } else {
      onChange?.(option.value);
      setOpen(false);
    }
    setQuery("");
  }

  function createOption(): void {
    if (!query.trim() || !onCreate) return;
    onCreate(query.trim());
    setQuery("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const current = enabledIndexes.indexOf(activeIndex);
      const next =
        !open || current < 0
          ? direction > 0
            ? enabledIndexes[0]
            : enabledIndexes.at(-1)
          : enabledIndexes[
              (current + direction + enabledIndexes.length) %
                enabledIndexes.length
            ];
      setOpen(true);
      setActiveIndex(next ?? -1);
    } else if (open && (event.key === "Home" || event.key === "End")) {
      event.preventDefault();
      setActiveIndex(
        event.key === "Home"
          ? (enabledIndexes[0] ?? -1)
          : (enabledIndexes.at(-1) ?? -1),
      );
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) choose(option);
      else createOption();
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={cn("relative min-w-0", className)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
        }}
      >
        <div
          className={cn(
            "flex min-h-11 min-w-0 flex-wrap items-center gap-x-1 gap-y-2 studio-field rounded-md px-2 py-2 transition-[background-color,border-color] duration-150 sm:min-h-10 sm:py-1",
            disabled && "bg-canvas",
          )}
        >
          {multiple
            ? selected.map((selectedValue) => {
                const option = options.find(
                  (item) => item.value === selectedValue,
                );
                if (!option) return null;
                return (
                  <span
                    key={option.value}
                    data-color={option.color ? "" : undefined}
                    style={option.color ? { "--tag-color": option.color } as CSSProperties : undefined}
                    className="studio-combobox-item inline-flex min-w-0 max-w-full items-center gap-1 rounded-lg pl-2 text-xs text-ink-soft"
                  >
                    {option.color ? <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: option.color }} /> : null}
                    <span className="min-w-0 truncate">{option.label}</span>
                    <button
                      type="button"
                      aria-label={localeText("interface.remove_value0_4945934f", { defaultValue: "Remove {{value0}}", value0: option.label })}
                      disabled={disabled}
                      className="studio-tag-remove relative grid size-5 shrink-0 place-items-center rounded-md text-ink-muted outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={() => {
                        onValuesChange?.(
                          selected.filter((item) => item !== option.value),
                        );
                        requestAnimationFrame(() => inputRef.current?.focus());
                      }}
                    >
                      <CloseIcon className="size-3" />
                    </button>
                  </span>
                );
              })
            : null}
          <input
            ref={inputRef}
            id={`${id}-input`}
            name={name}
            role="combobox"
            data-multiple={multiple || undefined}
            aria-label={label}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-busy={loading || undefined}
            aria-controls={`${id}-listbox`}
            aria-activedescendant={
              open && filtered[activeIndex] && !filtered[activeIndex].disabled
                ? `${id}-option-${activeIndex}`
                : undefined
            }
            autoComplete="off"
            disabled={disabled}
            value={query}
            placeholder={
              multiple || !value
                ? placeholder
                : options.find((item) => item.value === value)?.label
            }
            className="studio-combobox-input min-h-7 min-w-[8rem] flex-1 border-0 bg-transparent px-1 font-sans text-ink outline-none placeholder:text-ink-muted"
            onFocus={() => {
              const selectedIndex = filtered.findIndex(
                (option) => option.value === value && !option.disabled,
              );
              setActiveIndex(
                selectedIndex >= 0
                  ? selectedIndex
                  : (enabledIndexes[0] ?? -1),
              );
              setOpen(true);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
              setOpen(true);
            }}
            onKeyDown={onKeyDown}
          />
          {loading ? (
            <span
              aria-hidden="true"
              className="size-[13px] animate-spin rounded-full border border-line border-t-ink motion-reduce:animate-none"
            />
          ) : null}
        </div>
        <span role="status" aria-atomic="true" className="sr-only">
          {loading
            ? localeText("interface.loading_options_556a19bb", { defaultValue: "Loading options." })
            : open
              ? localeText("interface._value0_options_available_c9bcd6c5", { defaultValue: "{{value0}} options available.", value0: enabledIndexes.length })
              : ""}
        </span>
      </div>
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              id={`${id}-listbox`}
              role="listbox"
              aria-label={label}
              aria-multiselectable={multiple || undefined}
              aria-busy={loading || undefined}
              style={listboxStyle}
              className="studio-combobox-panel z-50 overflow-y-auto studio-overlay rounded-lg p-1.5"
            >
              {loading ? (
                <div className="px-3 py-2 text-xs text-ink-muted">{localeText("interface.loading_33ce4174", { defaultValue: "Loading…" })}</div>
              ) : filtered.length ? (
                filtered.map((option, index) => (
                  <button
                    key={option.value}
                    id={`${id}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected.includes(option.value)}
                    disabled={option.disabled}
                    tabIndex={-1}
                    data-highlighted={index === activeIndex || undefined}
                    className={cn(
                      "studio-dropdown-option flex min-h-11 w-full items-center gap-2 rounded-sm px-2.5 text-start text-[13.5px] text-ink outline-none hover:bg-canvas-soft focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--focus-ring)] disabled:text-ink-faint sm:min-h-8",
                      index === activeIndex && "bg-canvas-soft",
                    )}
                    onPointerMove={() => setActiveIndex(index)}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <span
                      className="w-3 shrink-0 font-semibold"
                      aria-hidden="true"
                    >
                      {selected.includes(option.value) ? <CheckIcon className="size-3.5 shrink-0" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 break-words">
                      {option.color ? <span aria-hidden="true" className="mr-2 inline-block size-2 rounded-full" style={{ backgroundColor: option.color }} /> : null}
                      {option.label}
                    </span>
                  </button>
                ))
              ) : (
                <div className="flex flex-col items-start gap-2 break-words px-3 py-2 text-[13px] text-ink-soft">
                  <span>{localeText("interface.sentence_no_results_for_value0__23870fe9", { defaultValue: "No results for “{{value0}}”.", value0: query })}</span>
                  {onCreate && query.trim() ? (
                    <button
                      type="button"
                      className="min-h-11 rounded-sm bg-canvas-soft px-3 text-xs font-semibold text-ink sm:min-h-[30px]"
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={createOption}
                    >{localeText("interface.create_15f8f8dd", { defaultValue: "Create “" })}{query.trim()}”
                    </button>
                  ) : null}
                </div>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
